import { Navigate, useLocation } from 'react-router';
import { useAuth } from './AuthContext';
import { Permission, useHasPermission } from '@/utils/roles';
import { NoAccessPage } from '@/components';
import { AUTH_PATHS } from '@/feature/authentication/constants';
import { useMfaSetupGuard } from '@/feature/authentication/hooks/useMfaSetupGuard';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: Permission[];
}

export const ProtectedRoute = ({
  children,
  requiredPermissions = [],
}: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const location = useLocation();
  const {
    data: mfaGuard,
    isLoading: mfaGuardLoading,
    isFetched: mfaGuardFetched,
  } = useMfaSetupGuard(user?.email);
  const shouldCheckPermissions =
    !authLoading && isAuthenticated && requiredPermissions.length > 0;
  const {
    hasPermission,
    isLoading: userLoading,
    currentUser,
  } = useHasPermission(requiredPermissions ?? [], true, {
    enabled: shouldCheckPermissions,
  });

  const setupRequired = mfaGuard?.setupRequired === true;
  const isCheckingMfaSetup =
    isAuthenticated && !authLoading && mfaGuardLoading && !mfaGuardFetched;

  if (authLoading || userLoading || isCheckingMfaSetup) {
    return (
      <div
        className="flex h-screen items-center justify-center text-lg"
        aria-label="Loading"
        tabIndex={0}
      >
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (setupRequired && location.pathname !== AUTH_PATHS.MFA_SETUP_PHONE) {
    return <Navigate to={AUTH_PATHS.MFA_SETUP_PHONE} replace />;
  }

  if (requiredPermissions.length > 0 && currentUser && !hasPermission) {
    return <NoAccessPage />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
